import { addError } from '@/store/reducers/ToastStore';
import { createThread, deleteThread } from '@/store/reducers/ChatStore';
import { getChatAPI } from '@/api/ChatApi';
import { mkThreadLinkTrackAttrs } from '@/analytics/props/threadBar';
import { setCurrentThread } from '@/store/reducers/UIState';
import { Thread } from '@/models/Thread';
import { useAppDispatch } from '@/store/hooks';

import { alpha, IconButton, List, ListItem, Typography } from '@mui/material';
import { Link, useNavigate } from 'react-router-dom';
import DeleteIcon from '@mui/icons-material/Delete';
import styled from 'styled-components';

type ConversationListProps = {
  conversations: Thread[];
  selectedConversationId?: string | null;
};

export const ConversationList = ({
  conversations,
  selectedConversationId = null,
}: ConversationListProps) => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const chatApi = getChatAPI();

  const onDeleteThread = async (thread: Thread): Promise<void> => {
    // optimistically delete thread from store
    dispatch(deleteThread(thread.id));
    dispatch(setCurrentThread(null));
    // navigate to home page url
    navigate('/chat');

    try {
      await chatApi.archiveThread(thread.id);
    } catch (error) {
      dispatch(addError({ text: 'Error deleting thread. Please try again.' }));
      // if deletion fails, add thread back to store
      dispatch(createThread(thread));
    }
  };

  return (
    <ConversationsWrapper>
      {conversations.map((thread, index) => (
        <ConversationItem
          key={thread.id}
          data-testid="convo-list-item"
          {...mkThreadLinkTrackAttrs({
            threadId: thread.id,
            threadIndex: index,
            renderedThreadCount: conversations.length,
          })}
        >
          <ConversationPreview
            isSelected={thread.id === selectedConversationId}
          >
            <LinkWrapper
              key={thread.id}
              to={`/chat/${thread.id}`}
              $isSelected={thread.id === selectedConversationId}
            >
              <Typography noWrap variant="body2">
                {thread.messages[0]?.strippedText}
              </Typography>
            </LinkWrapper>
            <DeleteThreadButton onClick={() => onDeleteThread(thread)}>
              <DeleteIcon sx={{ height: '21px' }} />
            </DeleteThreadButton>
          </ConversationPreview>
        </ConversationItem>
      ))}
    </ConversationsWrapper>
  );
};

const ConversationsWrapper = styled(List)({
  display: 'flex',
  flexDirection: 'column',
  flexGrow: 1,
});

const ConversationItem = styled(ListItem)(({ theme }) => ({
  '&.MuiListItem-root': {
    padding: theme.spacing(0, 0.5),
  },
}));

const ConversationPreview = styled('div')<{ isSelected: boolean }>(
  ({ theme, isSelected }) => `
    align-items: center;
    background-color: ${
      isSelected ? alpha(theme.color['teal-100'].hex, 0.4) : 'inherit'
    };
    border-radius: 4px;
    display: flex;
    padding: ${theme.spacing(0.75, 1.5)};
    width: 100%;

    &:hover {
      background-color: ${alpha(theme.color['teal-100'].hex, 0.4)};
    }

    &:hover ${LinkWrapper} {
      width: 93%;
    }

    &:hover ${DeleteThreadButton} {
      &.MuiButtonBase-root {
        display: flex;
      }
    }
  `,
);

const LinkWrapper = styled(Link)<{ $isSelected: boolean }>(
  ({ theme, $isSelected }) => `
    color: ${
      $isSelected ? theme.palette.secondary.main : theme.color['cream-100'].hex
    };
    text-decoration: none;
    width: 100%;

    &:hover {
      color: ${theme.color['cream-100'].hex};
    }
  `,
);

const DeleteThreadButton = styled(IconButton)`
  &.MuiButtonBase-root {
    display: none;
    padding: 0;

    & .MuiSvgIcon-root {
      color: ${({ theme }) => theme.color['cream-100'].hex};

      &:hover {
        color: ${({ theme }) => theme.color['gray-30'].hex};
      }
    }
  }
`;
